<template>
  <div class="navbar-container d-flex content align-items-center">
    <!-- Nav Menu Toggler -->
    <ul class="nav navbar-nav d-xl-none">
      <li class="nav-item">
        <b-link
          class="nav-link menu-toggle-btn"
          @click="toggleVerticalMenuActive"
        >
          <feather-icon
            icon="MenuIcon"
            size="21"
            class="menu-icon"
          />
        </b-link>
      </li>
    </ul>

    <!-- Left Col -->
    <div class="bookmark-wrapper align-items-center flex-grow-1 d-none d-lg-flex">
      <h4 class="mb-0 text-primary">Gescom Portal</h4>
    </div>

    <!-- Right side with notifications and user menu -->
    <b-navbar-nav class="nav align-items-center ml-auto">
      <!-- Notifications -->
      <b-nav-item-dropdown
        right
        toggle-class="nav-link d-flex align-items-center"
        class="notification-dropdown"
      >
        <template #button-content>
          <feather-icon
            icon="BellIcon"
            size="20"
            class="text-muted"
          />
          <b-badge
            v-if="unreadNotifications > 0"
            variant="danger"
            pill
            class="notification-badge"
          >
            {{ unreadNotifications }}
          </b-badge>
        </template>

        <div class="notification-header">
          <h6 class="mb-0">Notificaciones</h6>
          <b-button
            variant="link"
            size="sm"
            @click="goToNotifications"
          >
            Ver todas
          </b-button>
        </div>

        <div class="notification-list">
          <div
            v-for="notification in recentNotifications"
            :key="notification.id"
            class="notification-item"
            :class="{ 'unread': !notification.read }"
            @click="markAsRead(notification.id)"
          >
            <div class="notification-icon">
              <feather-icon
                :icon="getNotificationIcon(notification.type)"
                size="16"
                :class="getNotificationClass(notification.type)"
              />
            </div>
            <div class="notification-content">
              <p class="notification-title">{{ notification.title }}</p>
              <small class="notification-time">{{ formatTime(notification.time) }}</small>
            </div>
          </div>
        </div>

        <div v-if="recentNotifications.length === 0" class="text-center py-3">
          <p class="text-muted mb-0">No hay notificaciones</p>
        </div>
      </b-nav-item-dropdown>

      <!-- User Menu -->
      <b-nav-item-dropdown
        right
        toggle-class="d-flex align-items-center dropdown-user-link"
        class="dropdown-user"
      >
        <template #button-content>
          <!-- Icono siempre visible (también en móvil) -->
          <span class="d-inline-flex align-items-center mr-50" style="min-width:20px;" title="Perfil de usuario" aria-label="Perfil de usuario">
            <feather-icon icon="UserIcon" size="20" class="text-body" />
          </span>
          <!-- Texto del usuario (solo en >= sm) -->
          <div class="d-sm-flex d-none user-nav">
            <feather-icon icon="UserIcon" size="18" class="mr-50 text-primary" />
            <p class="user-name font-weight-bolder mb-0">
              {{ userProfile.nombre || 'Usuario' }}
            </p>
            <span class="user-status">{{ userProfile.rol || 'Usuario' }}</span>
          </div>
        </template>

        <b-dropdown-item link-class="d-flex align-items-center" @click="goToProfile">
          <feather-icon
            size="16"
            icon="UserIcon"
            class="mr-50"
          />
          <span>Mi Perfil</span>
        </b-dropdown-item>

        <b-dropdown-item link-class="d-flex align-items-center" @click="goToSettings">
          <feather-icon
            size="16"
            icon="SettingsIcon"
            class="mr-50"
          />
          <span>Configuración</span>
        </b-dropdown-item>

        <b-dropdown-item link-class="d-flex align-items-center" @click="goToNotifications">
          <feather-icon
            size="16"
            icon="BellIcon"
            class="mr-50"
          />
          <span>Notificaciones</span>
        </b-dropdown-item>

        <b-dropdown-divider />

        <b-dropdown-item link-class="d-flex align-items-center" @click="handleLogout">
          <feather-icon
            size="16"
            icon="LogOutIcon"
            class="mr-50"
          />
          <span>Cerrar Sesión</span>
        </b-dropdown-item>
      </b-nav-item-dropdown>
    </b-navbar-nav>
  </div>
</template>

<script>
import {
  BLink, BNavbarNav, BNavItemDropdown, BDropdownItem, BDropdownDivider, BBadge, BButton,
} from 'bootstrap-vue'
import authService from '@/services/authService'
import axios from '@/libs/axios'
import notificationService from '@/services/notificationService'

export default {
  components: {
    BLink,
    BNavbarNav,
    BNavItemDropdown,
    BDropdownItem,
    BDropdownDivider,
    BBadge,
    BButton,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      userProfile: {
        nombre: '',
        rol: 'Administrador',
      },
      recentNotifications: [],
      notificationInterval: null,
    }
  },
  computed: {
    unreadNotifications() {
      return this.recentNotifications.filter(n => !n.read).length
    },
  },
  mounted() {
    this.loadUserProfile()
    this.initializeNotifications()
  },
  beforeDestroy() {
    notificationService.removeListener(this.onNotificationsUpdate)
    notificationService.stopPolling()
  },
  methods: {
    getNotificationIcon(type) {
      const icons = {
        project: 'FolderIcon',
        activity: 'CheckSquareIcon',
        work: 'BuildingIcon',
        system: 'SettingsIcon',
        reminder: 'ClockIcon',
        update: 'DownloadIcon',
      }
      return icons[type] || 'BellIcon'
    },
    getNotificationClass(type) {
      const classes = {
        project: 'text-primary',
        activity: 'text-success',
        work: 'text-info',
        system: 'text-warning',
        reminder: 'text-secondary',
        update: 'text-success',
      }
      return classes[type] || 'text-secondary'
    },
    formatTime(time) {
      const now = new Date()
      const notificationTime = new Date(time)
      const diffMs = now - notificationTime
      const diffMins = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMins < 1) return 'Ahora'
      if (diffMins < 60) return `Hace ${diffMins} min`
      if (diffHours < 24) return `Hace ${diffHours} h`
      if (diffDays < 7) return `Hace ${diffDays} días`
      return notificationTime.toLocaleDateString('es-ES')
    },
    markAsRead(notificationId) {
      const notification = this.recentNotifications.find(n => n.id === notificationId)
      if (notification) {
        notification.read = true
      }
    },
    goToProfile() {
      this.$router.push({ name: 'profile' })
    },
    goToSettings() {
      this.$router.push({ name: 'configuracion-general' })
    },
    goToNotifications() {
      this.$router.push({ name: 'notifications' })
    },
    handleLogout() {
      this.$swal({
        title: '¿Estás seguro?',
        text: '¿Deseas cerrar sesión?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, cerrar sesión',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.logout()
        }
      })
    },
    async loadUserProfile() {
      // 1) Intentar vía servicio oficial
      let user = authService.getCurrentUser()

      // 2) Fallback: algunos módulos guardan en 'userData'
      if (!user) {
        try {
          const userDataStr = localStorage.getItem('userData')
          if (userDataStr) user = JSON.parse(userDataStr)
        } catch (e) {}
      }

      // 3) Si aún no hay usuario pero hay token, intentar cargarlo del backend
      if (!user && authService.getToken && authService.getToken()) {
        try {
          // Intento vía servicio
          const loaded = await authService.loadCurrentUser()
          if (loaded) user = loaded
        } catch (e) {}
        // Intento directo por si el servicio falla o hay doble implementación
        if (!user) {
          try {
            const { data } = await axios.get('/usuarios/current')
            if (data) {
              user = data
              // Guardar para siguientes veces
              try { authService.setCurrentUser?.(data) } catch (e) {}
            }
          } catch (e) {}
        }
      }

      if (user) {
        const nombrePreferido = user.nombreCompleto || user.fullName || [user.nombre, user.primerApellido, user.segundoApellido].filter(Boolean).join(' ')
        const rolPreferido = user.rol?.nombre || user.role || 'Usuario'

        this.userProfile = {
          nombre: (nombrePreferido || '').trim(),
          rol: rolPreferido,
        }
      }
    },
    logout() {
      // El logout de authService ya redirige a /gestioncomercial/login
      authService.logout()
    },
    async loadNotifications() {
      try {
        // Simular carga de notificaciones desde el backend
        // En una implementación real, esto sería una llamada HTTP
        const mockNotifications = [
          {
            id: Date.now(),
            title: 'Bienvenido al sistema Gescom',
            type: 'system',
            time: new Date().toISOString(),
            read: false,
          }
        ]
        this.recentNotifications = mockNotifications
      } catch (error) {
        console.error('Error al cargar notificaciones:', error)
      }
    },
    startNotificationPolling() {
      // Verificar nuevas notificaciones cada 30 segundos
      this.notificationInterval = setInterval(() => {
        this.checkForNewNotifications()
      }, 30000)
    },
    checkForNewNotifications() {
      // Simular verificación de nuevas notificaciones
      const shouldShowNotification = Math.random() < 0.3 // 30% de probabilidad
      
      if (shouldShowNotification) {
        this.generateRandomNotification()
      }
    },
    generateRandomNotifications() {
      // Generar algunas notificaciones iniciales
      setTimeout(() => {
        this.generateRandomNotification()
      }, 5000) // Primera notificación después de 5 segundos
      
      setTimeout(() => {
        this.generateRandomNotification()
      }, 15000) // Segunda notificación después de 15 segundos
    },
    generateRandomNotification() {
      const notificationTypes = [
        {
          type: 'project',
          title: 'Nuevo proyecto asignado',
          icon: 'FolderIcon',
          class: 'text-primary'
        },
        {
          type: 'activity',
          title: 'Actividad completada',
          icon: 'CheckSquareIcon',
          class: 'text-success'
        },
        {
          type: 'work',
          title: 'Obra en progreso',
          icon: 'BuildingIcon',
          class: 'text-info'
        },
        {
          type: 'system',
          title: 'Mantenimiento programado',
          icon: 'SettingsIcon',
          class: 'text-warning'
        },
        {
          type: 'reminder',
          title: 'Recordatorio: Revisar documentos pendientes',
          icon: 'ClockIcon',
          class: 'text-secondary'
        },
        {
          type: 'update',
          title: 'Sistema actualizado',
          icon: 'DownloadIcon',
          class: 'text-success'
        }
      ]
      
      const randomType = notificationTypes[Math.floor(Math.random() * notificationTypes.length)]
      
      const newNotification = {
        id: Date.now() + Math.random(),
        title: randomType.title,
        type: randomType.type,
        time: new Date().toISOString(),
        read: false,
      }
      
      // Agregar al inicio de la lista
      this.recentNotifications.unshift(newNotification)
      
      // Limitar a 10 notificaciones máximo
      if (this.recentNotifications.length > 10) {
        this.recentNotifications = this.recentNotifications.slice(0, 10)
      }
      
      // Mostrar toast de notificación
      this.$toast({
        component: {
          template: `<div><i class="feather ${randomType.icon} mr-2"></i>${newNotification.title}</div>`,
        },
        props: {
          variant: 'info',
          duration: 5000,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.navbar-container {
  padding: 0 1.5rem;
  height: 4rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.user-nav {
  text-align: right;
  
  .user-name {
    font-size: 0.875rem;
    color: #2c3e50;
    margin-bottom: 0;
  }
  
  .user-status {
    font-size: 0.75rem;
    color: #6c757d;
  }
}

.dropdown-user-link {
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
}

.notification-dropdown {
  position: relative;
  
  .notification-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    font-size: 0.7rem;
    min-width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.notification-list {
  max-height: 300px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f3f4;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &.unread {
    background-color: #f0f8ff;
    border-left: 3px solid #007bff;
  }
  
  .notification-icon {
    margin-right: 0.75rem;
    margin-top: 0.125rem;
  }
  
  .notification-content {
    flex: 1;
    
    .notification-title {
      font-size: 0.875rem;
      color: #2c3e50;
      margin-bottom: 0.25rem;
      line-height: 1.3;
    }
    
    .notification-time {
      color: #6c757d;
      font-size: 0.75rem;
    }
  }
}

// Estilos para el botón del menú móvil
.menu-toggle-btn {
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  .menu-icon {
    color: #1a1a1a;
    transition: color 0.2s ease;
  }
  
  &:hover .menu-icon {
    color: #007bff;
  }
}

@media (max-width: 768px) {
  .user-nav {
    display: none !important;
  }
  
  .navbar-container {
    padding: 0 1rem;
  }
  
  .menu-toggle-btn {
    padding: 0.75rem;
    
    .menu-icon {
      font-size: 1.25rem;
    }
  }
}
</style>
